import {
  EventStreamContentType,
  fetchEventSource,
  type FetchEventSourceInit,
} from '@microsoft/fetch-event-source';

import { getCookie } from '@/app/actions/cookies';
import { ReferenceItem } from '@/app/services/llm';
import { SearchModel, siteConfig } from '@/config/site';
import clientRequest from '@/lib/client-request';
import { getReferenceDocId } from '@/lib/reference-doc-id';

export type ChatSummaryStyle = 'academic' | 'simple';

export interface ChatAuthor {
  name?: string;
  fore_name?: string;
  last_name?: string;
  initials?: string;
  affiliations?: Array<{
    name: string;
  }>;
}

export interface ChatRefDoc {
  pmid?: string | null;
  pmcid?: string | null;
  openalex_id?: string | null;
  doi?: string | null;
  link?: string | null;
  title?: string | null;
  abstract?: string | null;
  authors?: ChatAuthor[] | null;
  publication?: string | null;
  publisher?: string | null;
  publisher_abbr?: string | null;
  pub_year?: number | null;
  pub_month?: number | null;
  pub_language?: string | null;
  pub_day?: string | null;
  impact_factor?: number | string | null;
  doc_id?: string | null;
  search_gateway?: string | null;
  pub_source_str?: string | null;
  figure_urls?: string[] | null;
  translation?: ReferenceItem['translation'];
}

export interface ChatSession {
  session_id: string;
  user_id: string;
  title: string | null;
  title_source: 'auto' | 'llm' | 'user';
  created_at: string;
  updated_at: string;
}

export interface ChatStreamErrorInfo {
  code: string;
  message?: string | null;
  invalid_types?: string[] | null;
}

export interface ChatMessage {
  message_id: string;
  session_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  message_type?: 'chat' | 'translation' | 'outline' | null;
  parent_message_id?: string | null;
  retry_of?: string | null;
  target_lang?: string | null;
  content?: string | null;
  status?: 'pending' | 'streaming' | 'done' | 'failed' | null;
  search_mode?: SearchModel | null;
  summary_style?: ChatSummaryStyle | null;
  sources?: string[] | null;
  ref_docs?: ChatRefDoc[] | null;
  upstream_log_id?: string | null;
  cost?: number | null;
  stream_error?: ChatStreamErrorInfo | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CreateChatSessionReq {
  query: string;
  search_mode: SearchModel;
  summary_style: ChatSummaryStyle;
  sources?: string[] | null;
  metadata?: Record<string, unknown>;
}

export interface CreateChatSessionResp {
  session_id: string;
  user_message_id: string;
  assistant_message_id: string;
  title: string;
}

export interface AddChatMessageReq {
  query: string;
  search_mode: SearchModel;
  summary_style: ChatSummaryStyle;
  sources?: string[] | null;
  metadata?: Record<string, unknown>;
}

export interface AddChatMessageResp {
  user_message_id: string;
  assistant_message_id: string;
}

export interface TranslateInitReq {
  target_lang: string;
}

export interface TranslateInitResp {
  user_message_id: string;
  assistant_message_id: string;
}

export interface RetryChatMessageResp {
  assistant_message_id: string;
}

export interface ChatPublishedPost {
  post_id: string;
  snapshot_id: string;
  token: string;
  title: string;
  summary?: string | null;
  cover_url?: string | null;
  author_display_name: string;
  anonymous: boolean;
  indexable: boolean;
  current_user_liked: boolean;
  like_count: number;
  view_count: number;
  published_at: string;
  updated_at: string;
}

export interface ChatSnapshotDetail {
  snapshot_id: string;
  share_token: string;
  share_url: string;
  snapshot_version: number;
  published_post?: ChatPublishedPost | null;
  square_url?: string | null;
}

export interface PublishChatSnapshotReq {
  title?: string;
  summary?: string;
  cover_url?: string;
  anonymous?: boolean;
  indexable?: boolean;
}

export interface PublishChatSnapshotResp {
  snapshot_id: string;
  post_id: string;
  token: string;
  status: string;
  share_url: string;
  square_url: string;
}

export interface ChatSessionListResp {
  items: ChatSession[];
  total: number;
  offset: number;
  size: number;
}

export interface QueryCheckResp {
  valid: boolean;
  invalid_types: string[];
}

export type ChatStreamEvent =
  | {
      type: 'ready';
      session_id: string;
      message_id: string;
    }
  | {
      type: 'stage';
      name: 'analyzing' | 'searching' | 'writing';
    }
  | {
      type: 'citations';
      items: ChatRefDoc[];
    }
  | {
      type: 'answer';
      delta: string;
    }
  | {
      type: 'title_updated';
      session_id: string;
      title: string;
    }
  | {
      type: 'done';
      message_id: string;
      cost: number;
    }
  | {
      type: 'stream_end';
      message_id: string;
    }
  | {
      type: 'error';
      code: string;
      message?: string;
      invalid_types?: string[];
    };

export interface StreamChatParams {
  sessionId: string;
  assistantMessageId: string;
  onEvent: (event: ChatStreamEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error | string | number) => void;
  abortController?: AbortController;
  /** 稳定 id，刷新不变；同 message 多标签页区分用。默认 sessionStorage 生成。 */
  clientId?: string;
  /** 断线重连时上次收到的最大 SSE event id；首次连接传 null。 */
  lastEventId?: number | null;
}

interface FetchEventSourceMessage {
  data: string;
  id?: string;
}

interface FetchEventSourceLike {
  (
    input: string,
    init: FetchEventSourceInit & {
      onmessage: (message: FetchEventSourceMessage) => void;
    },
  ): Promise<void>;
}

export function createChatSession(
  req: CreateChatSessionReq,
): Promise<DataResponse<CreateChatSessionResp>> {
  return clientRequest.post('/v3/chat/sessions', req);
}

export function addChatMessage(
  sessionId: string,
  req: AddChatMessageReq,
): Promise<DataResponse<AddChatMessageResp>> {
  return clientRequest.post(`/v3/chat/sessions/${sessionId}/messages`, req);
}

export function listChatSessions({
  offset,
  size,
  keyword,
}: {
  offset: number;
  size: number;
  keyword?: string;
}): Promise<DataResponse<ChatSessionListResp>> {
  return clientRequest.get('/v3/chat/sessions', {
    params: { offset, size, keyword },
  });
}

export function getChatSession(
  sessionId: string,
): Promise<DataResponse<ChatSession>> {
  return clientRequest.get(`/v3/chat/sessions/${sessionId}`);
}

export function getChatMessages(
  sessionId: string,
): Promise<DataResponse<ChatMessage[]>> {
  return clientRequest.get(`/v3/chat/sessions/${sessionId}/messages`);
}

export function deleteChatSession(
  sessionId: string,
): Promise<DataResponse<null>> {
  return clientRequest.delete(`/v3/chat/sessions/${sessionId}`);
}

export function renameChatSession(
  sessionId: string,
  title: string,
): Promise<DataResponse<null>> {
  return clientRequest.patch(`/v3/chat/sessions/${sessionId}`, { title });
}

export function initTranslationMessage(
  parentMessageId: string,
  req: TranslateInitReq,
): Promise<DataResponse<TranslateInitResp>> {
  return clientRequest.post(`/v3/chat/messages/${parentMessageId}/translate`, req);
}

export function retryChatMessage(
  failedAssistantMessageId: string,
): Promise<DataResponse<RetryChatMessageResp>> {
  return clientRequest.post(`/v3/chat/messages/${failedAssistantMessageId}/retry`);
}

export function createChatSnapshot(
  sessionId: string,
): Promise<DataResponse<ChatSnapshotDetail>> {
  return clientRequest.post(`/v3/chat/sessions/${sessionId}/snapshots`);
}

export function publishChatSession(
  sessionId: string,
  req?: PublishChatSnapshotReq,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/chat/sessions/${sessionId}/publish`, req);
}

export function refreshChatSnapshot(
  snapshotId: string,
): Promise<DataResponse<ChatSnapshotDetail>> {
  return clientRequest.post(`/v3/chat/snapshots/${snapshotId}/refresh`);
}

export function publishChatSnapshot(
  snapshotId: string,
  req?: PublishChatSnapshotReq,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/chat/snapshots/${snapshotId}/publish`, req);
}

export function unpublishChatSnapshot(
  snapshotId: string,
): Promise<DataResponse<PublishChatSnapshotResp>> {
  return clientRequest.post(`/v3/chat/snapshots/${snapshotId}/unpublish`);
}

export function revokeChatSnapshot(
  snapshotId: string,
): Promise<DataResponse<null>> {
  return clientRequest.post(`/v3/chat/snapshots/${snapshotId}/revoke`);
}

export function normalizeChatMessage(message: ChatMessage): ChatMessage {
  const metadata = message.metadata ?? null;
  const normalizedMetadata = { ...(metadata ?? {}) };

  const code =
    typeof metadata?.last_stream_error_code === 'string'
      ? metadata.last_stream_error_code
      : null;
  const targetLang =
    message.target_lang ??
    (typeof metadata?.target_lang === 'string' ? metadata.target_lang : null);
  const retryOf =
    message.retry_of ??
    (typeof metadata?.retry_of === 'string' ? metadata.retry_of : null);

  delete normalizedMetadata.last_stream_error_code;
  delete normalizedMetadata.last_stream_error_message;
  delete normalizedMetadata.last_stream_invalid_types;
  delete normalizedMetadata.target_lang;
  delete normalizedMetadata.retry_of;

  return {
    ...message,
    retry_of: retryOf,
    target_lang: targetLang,
    stream_error:
      message.stream_error ??
      (code
        ? {
            code,
            message:
              typeof metadata?.last_stream_error_message === 'string'
                ? metadata.last_stream_error_message
                : null,
            invalid_types: Array.isArray(metadata?.last_stream_invalid_types)
              ? metadata.last_stream_invalid_types.filter(
                  (type): type is string => typeof type === 'string',
                )
              : null,
          }
        : null),
    metadata:
      Object.keys(normalizedMetadata).length > 0 ? normalizedMetadata : null,
  };
}

export function getChatMessageStreamError(
  message: ChatMessage,
): ChatStreamErrorInfo | null {
  return message.stream_error ?? null;
}

export function getChatMessageInvalidTypes(message: ChatMessage): string[] {
  return Array.isArray(message.stream_error?.invalid_types)
    ? message.stream_error.invalid_types.filter(
        (type): type is string => typeof type === 'string',
      )
    : [];
}

export function getChatMessageTargetLang(message: ChatMessage): string | null {
  return message.target_lang ?? null;
}

export function getChatMessageRetryOf(message: ChatMessage): string | null {
  return message.retry_of ?? null;
}

function parseChatStreamEvent(data: string): ChatStreamEvent {
  return JSON.parse(data) as ChatStreamEvent;
}

export async function streamChatWithDeps(
  params: StreamChatParams,
  deps: {
    apiBaseUrl: string;
    getToken: () => Promise<string>;
    fetchEventSource: FetchEventSourceLike;
  },
): Promise<void> {
  let didClose = false;
  let lastSeq: number | null =
    typeof params.lastEventId === 'number' ? params.lastEventId : null;

  try {
    await deps.fetchEventSource(`${deps.apiBaseUrl}/v3/chat/stream`, {
      method: 'POST',
      headers: {
        Accept: EventStreamContentType,
        'Content-Type': 'application/json',
        'X-Auth-Token': await deps.getToken(),
      },
      body: JSON.stringify({
        session_id: params.sessionId,
        assistant_message_id: params.assistantMessageId,
        client_id: params.clientId,
        last_event_id: lastSeq,
      }),
      signal: params.abortController?.signal,
      openWhenHidden: true,
      async onopen(response) {
        const contentType = response.headers.get('content-type') ?? '';
        if (response.ok && contentType.includes(EventStreamContentType)) {
          params.onOpen?.();
          return;
        }
        throw new Error(`Unexpected response ${response.status}`);
      },
      onmessage(message) {
        if (message.id) {
          const parsed = Number(message.id);
          if (Number.isFinite(parsed)) lastSeq = parsed;
        }
        params.onEvent(parseChatStreamEvent(message.data));
      },
      onclose() {
        didClose = true;
        params.onClose?.();
      },
      onerror(error) {
        params.onError?.(error);
        throw error;
      },
    });
  } finally {
    if (!didClose) {
      params.onClose?.();
    }
  }
}

export async function streamChat(params: StreamChatParams): Promise<void> {
  return streamChatWithDeps(params, {
    apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL ?? '',
    getToken: async () => (await getCookie(siteConfig.tokenName)) ?? '',
    fetchEventSource,
  });
}

export function toReferenceItem(ref: ChatRefDoc): ReferenceItem {
  const docId = getReferenceDocId(ref);
  return {
    pmcid: ref.pmcid ?? undefined,
    pmid: ref.pmid ?? '',
    openalex_id: ref.openalex_id ?? undefined,
    figure_urls: ref.figure_urls ?? undefined,
    pub_day: ref.pub_day ?? '',
    title: ref.title ?? '',
    abstract: ref.abstract ?? '',
    publication: ref.publication ?? undefined,
    publisher: ref.publication ?? ref.publisher ?? '',
    publisher_abbr: ref.publisher_abbr ?? '',
    pub_year: ref.pub_year ?? 0,
    pub_month: ref.pub_month ?? 0,
    pub_language: ref.pub_language ?? undefined,
    link: ref.link ?? '',
    doi: ref.doi ?? '',
    authors: (ref.authors ?? []).map((author) => ({
      name: author.name ?? '',
      fore_name: author.fore_name ?? '',
      last_name: author.last_name ?? '',
      initials: author.initials ?? '',
      affiliations: author.affiliations,
    })),
    doc_id: docId,
    search_gateway: ref.search_gateway ?? undefined,
    pub_source_str:
      ref.pub_source_str ?? ref.publication ?? ref.publisher ?? '',
    translation: ref.translation,
  };
}
